import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Brain, Wallet, Rocket, TrendingUp, ChevronRight, CheckCircle, XCircle, Zap } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  budgeting: Wallet,
  investing: TrendingUp,
  entrepreneurship: Rocket,
};

const categoryColors = {
  budgeting: '#C0975C',
  investing: '#0A1F44',
  entrepreneurship: '#C0975C',
};

export default function QuizArena() {
  const { category } = useParams();
  const { token, refreshUser } = useAuth();
  const [categories, setCategories] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [result, setResult] = useState(null);
  const [score, setScore] = useState({ correct: 0, total: 0, xp: 0 });
  const [loading, setLoading] = useState(true);
  const [quizComplete, setQuizComplete] = useState(false);

  useEffect(() => {
    if (category) {
      fetchQuestions(category);
    } else {
      fetchCategories();
    }
  }, [category]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/quiz/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data.categories);
    } catch (error) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = async (cat) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/quiz/questions/${cat}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuestions(response.data);
      setCurrentIndex(0);
      setSelectedAnswer(null);
      setResult(null);
      setScore({ correct: 0, total: 0, xp: 0 });
      setQuizComplete(false);
    } catch (error) {
      toast.error('Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (selectedAnswer === null) return;

    try {
      const response = await axios.post(`${API}/quiz/submit`, {
        question_id: questions[currentIndex].id,
        selected_answer: selectedAnswer
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setResult(response.data);
      setScore(prev => ({
        correct: prev.correct + (response.data.correct ? 1 : 0),
        total: prev.total + 1,
        xp: prev.xp + response.data.xp_earned
      }));

      if (response.data.correct) {
        let message = `+${response.data.xp_earned} XP!`;
        if (response.data.streak_bonus > 0) {
          message += ` (includes ${response.data.streak_bonus} streak bonus)`;
        }
        toast.success(message);
        
        if (response.data.badges_earned?.length > 0) {
          response.data.badges_earned.forEach(badge => {
            toast.success(`New badge earned: ${badge}!`);
          });
        }
      }

      await refreshUser();
    } catch (error) {
      toast.error('Failed to submit answer');
    }
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setSelectedAnswer(null);
      setResult(null);
    } else {
      setQuizComplete(true);
    }
  };

  const currentQuestion = questions[currentIndex];

  // Category Selection View
  if (!category) {
    return (
      <Layout>
        <div className="p-6 lg:p-8 max-w-4xl mx-auto" data-testid="quiz-arena">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 
              className="text-3xl lg:text-4xl font-black mb-2"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              <span className="text-[#C0975C]">Quiz</span> Arena
            </h1>
            <p className="text-[#A3A3A3] mb-8">Choose a topic and test your financial knowledge!</p>

            <div className="grid gap-4">
              {loading ? (
                <div className="text-center py-12">
                  <div className="text-[#C0975C] animate-pulse">Loading...</div>
                </div>
              ) : (
                categories.map((cat) => {
                  const Icon = categoryIcons[cat.id] || Brain;
                  return (
                    <Link
                      key={cat.id}
                      to={`/quiz/${cat.id}`}
                      className="neo-card p-6 flex items-center gap-6 group card-hover"
                      data-testid={`quiz-category-${cat.id}`}
                    >
                      <div 
                        className="w-16 h-16 flex items-center justify-center group-hover:scale-110 transition-transform"
                        style={{ backgroundColor: cat.color }}
                      >
                        <Icon className="w-8 h-8 text-black" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-xl mb-1">{cat.name}</h3>
                        <p className="text-[#A3A3A3]">Test your knowledge in {cat.name.toLowerCase()}</p>
                      </div>
                      <ChevronRight className="w-6 h-6 text-[#A3A3A3] group-hover:text-[#C0975C] group-hover:translate-x-1 transition-all" />
                    </Link>
                  );
                })
              )}
            </div>
          </motion.div>
        </div>
      </Layout>
    );
  }

  // Quiz Complete View
  if (quizComplete) {
    return (
      <Layout>
        <div className="p-6 lg:p-8 max-w-2xl mx-auto" data-testid="quiz-complete">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="neo-card p-8 text-center"
          >
            <div className="w-20 h-20 bg-[#C0975C] flex items-center justify-center mx-auto mb-6">
              <Zap className="w-10 h-10 text-black" />
            </div>
            
            <h2 
              className="text-3xl font-black mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Quiz Complete!
            </h2>

            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="p-4 bg-white/5">
                <div className="text-3xl font-black text-[#C0975C]">{score.correct}</div>
                <div className="text-sm text-[#A3A3A3]">Correct</div>
              </div>
              <div className="p-4 bg-white/5">
                <div className="text-3xl font-black">{score.total}</div>
                <div className="text-sm text-[#A3A3A3]">Total</div>
              </div>
              <div className="p-4 bg-white/5">
                <div className="text-3xl font-black text-[#0A1F44]">+{score.xp}</div>
                <div className="text-sm text-[#A3A3A3]">XP Earned</div>
              </div>
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => fetchQuestions(category)}
                className="neo-button-secondary flex-1 py-3"
              >
                Try Again
              </button>
              <Link
                to="/quiz"
                className="neo-button-primary flex-1 py-3 text-center"
              >
                Pick New Topic
              </Link>
            </div>
          </motion.div>
        </div>
      </Layout>
    );
  }

  // Quiz Question View
  return (
    <Layout>
      <div className="p-6 lg:p-8 max-w-2xl mx-auto" data-testid="quiz-question-view">
        {loading ? (
          <div className="text-center py-12">
            <div className="text-[#C0975C] animate-pulse text-xl">Loading questions...</div>
          </div>
        ) : currentQuestion ? (
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            {/* Progress */}
            <div className="flex items-center justify-between mb-6">
              <Link to="/quiz" className="text-[#A3A3A3] hover:text-white text-sm">
                ← Back to topics
              </Link>
              <span className="text-[#A3A3A3]">
                Question {currentIndex + 1} of {questions.length}
              </span>
            </div>

            <div className="xp-bar h-2 mb-8">
              <div 
                className="xp-bar-fill" 
                style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }} 
              />
            </div>

            {/* Question */}
            <div className="neo-card p-6 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <span 
                  className="badge"
                  style={{ backgroundColor: categoryColors[category], color: '#000' }}
                >
                  {category}
                </span>
                <span className="badge badge-secondary">+{currentQuestion.xp_reward} XP</span>
              </div>
              
              <h2 
                className="text-xl lg:text-2xl font-bold mb-2"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {currentQuestion.question}
              </h2>
            </div>

            {/* Options */}
            <div className="space-y-3 mb-6">
              {currentQuestion.options.map((option, i) => {
                let optionClass = 'quiz-option';
                if (result) {
                  if (i === result.correct_answer) {
                    optionClass += ' correct';
                  } else if (i === selectedAnswer && !result.correct) {
                    optionClass += ' incorrect';
                  }
                } else if (selectedAnswer === i) {
                  optionClass += ' selected';
                }

                return (
                  <button
                    key={i}
                    onClick={() => !result && setSelectedAnswer(i)}
                    disabled={result !== null}
                    className={`${optionClass} w-full text-left flex items-center gap-4`}
                    data-testid={`quiz-option-${i}`}
                  >
                    <div className="w-8 h-8 border border-current flex items-center justify-center font-bold">
                      {String.fromCharCode(65 + i)}
                    </div>
                    <span>{option}</span>
                    {result && i === result.correct_answer && (
                      <CheckCircle className="w-5 h-5 text-[#C0975C] ml-auto" />
                    )}
                    {result && i === selectedAnswer && !result.correct && (
                      <XCircle className="w-5 h-5 text-[#FF3333] ml-auto" />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Result Explanation */}
            <AnimatePresence>
              {result && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className={`neo-card p-4 mb-6 border-l-4 ${result.correct ? 'border-[#C0975C]' : 'border-[#FF3333]'}`}
                >
                  <p className={`font-bold mb-2 flex items-center gap-2 ${result.correct ? 'text-[#C0975C]' : 'text-[#FF3333]'}`}>
                    {result.correct ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
                    {result.correct ? 'Correct!' : 'Not quite!'}
                  </p>
                  <p className="text-[#A3A3A3]">{result.explanation}</p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Action Button */}
            {!result ? (
              <button
                onClick={handleSubmit}
                disabled={selectedAnswer === null}
                className="neo-button-primary w-full py-4 disabled:opacity-50"
                data-testid="submit-answer-btn"
              >
                Submit Answer
              </button>
            ) : (
              <button
                onClick={handleNext}
                className="neo-button-primary w-full py-4"
                data-testid="next-question-btn"
              >
                {currentIndex < questions.length - 1 ? 'Next Question' : 'See Results'}
              </button>
            )}
          </motion.div>
        ) : (
          <div className="text-center py-12">
            <p className="text-[#A3A3A3]">No questions available for this category.</p>
            <Link to="/quiz" className="text-[#C0975C] mt-4 inline-block">
              ← Back to topics
            </Link>
          </div>
        )}
      </div>
    </Layout>
  );
}
